import { Button } from "components";
import { BankslipItem } from "components/BankslipItem";
import { IStagesPartner } from "models";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { document as documentMask, normalizeDocument } from "utils";
import * as S from "./styles";

import { Alert } from "antd";
import usePayment, { DATA_PAYMENT_DEFAULT } from "hooks/usePayment";
import { yupResolver } from "@hookform/resolvers/yup";
import { SchemaPartnerSearch } from "models/Schemas/Partner";
import { DetranItem } from "../providers/dto/get-detrans.dto";
import { useGetDetrans } from "../providers/get-detrans.provider";
import BoxFormDetran from "./components/BoxFormDetran";
import BoxFormPublicDepartment from "./components/BoxFormPublicDepartment";
import { useGetDebitsManager } from "./hooks/useGetDebitsManager";
import {
  EnumState,
  GetAllDebitsRequest,
} from "../providers/dto/get-debits.dto";
import {
  EnumPublicDepartaments,
  EnumPublicDepartmentSearchField,
  PublicDepartmentSearchFieldLabel,
} from "../types";
import { useSearchParams } from "react-router-dom";
import ModalAlert from "./components/ModalAlert";
import { useWhitelabel } from "hooks";
import { ModalDeleteDebits } from "./components/ModalDeleteDebits";

export const Search: React.FC<IStagesPartner> = ({
  setStage,
  enable,
  idEstablishment,
  setVariables,
}) => {
  const {
    dataPaymentBillet,
    clearPaymentBilletData,
    detran,
    setDetran,
    clearDetran,
    removeDebit,
    setUserId,
    dataPartnerSearch,
    setPaymentBilletData,
  } = usePayment();
  const { client } = useWhitelabel();

  const fields = [
    "license_plate",
    "document",
    "chassi",
    "renavam",
    "detran",
    "municipal_registration",
    "property_registration_number",
    "public_department_search_field",
  ];

  const [queryParameters, setQueryParameters] = useSearchParams();
  const license_plate_query = queryParameters.get("license_plate")?.trim();
  const renavan_query = queryParameters.get("renavam");
  const id_detran = queryParameters.get("id_detran");
  const [idDebitHovered, setDebitHovered] = useState<string | null>(null);
  const [openImpeditiveAlert, setOpenImpeditiveAlert] = useState(false);
  const [openModalDeleteDebits, setOpenModalDeleteDebits] = useState(false);
  const [debitsForRemove, setDebitsForRemove] = useState([] as any[]);

  const { data: detrans, isLoading: isLoadingGetPartnerDetran } = useGetDetrans(
    {
      id_establishment: idEstablishment,
    }
  );

  const methods = useForm({
    mode: "all",
    resolver: yupResolver(SchemaPartnerSearch),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = methods;

  const [
    license_plate,
    detranValue,
    renavam,
    document,
    municipal_registration,
    property_registration_number,
    public_department_search_field,
  ] = watch([
    "license_plate",
    "detran",
    "renavam",
    "document",
    "municipal_registration",
    "property_registration_number",
    "public_department_search_field",
  ]);

  const public_department = useMemo(() => {
    return queryParameters.get("public_department");
  }, [queryParameters]);

  const isPublicDepartment = useMemo(() => {
    return (
      !!public_department &&
      Object.values(EnumPublicDepartaments).includes(
        public_department as EnumPublicDepartaments
      )
    );
  }, [public_department]);

  const { mutate: getDebits, isLoading: isLoadingGetDebts } =
    useGetDebitsManager({
      license_plate,
      isPublicDepartment,
      setVariablesRequest: setVariables,
    });

  const isDisabled = useMemo(() => {
    if (isPublicDepartment && (document || municipal_registration || property_registration_number)) return false;

    if (detranValue && (license_plate || renavam || document)) return false;

    return true;
  }, [
    license_plate,
    detranValue,
    renavam,
    document,
    municipal_registration,
    property_registration_number,
    isPublicDepartment,
  ]);

  useEffect(() => {
    if (detrans && id_detran) {
      const detran = detrans.data.find((item) => item.id_partner === id_detran);

      if (detran) {
        setValue("detran", detran.state);
        handleShowLogoDetran(detran.state);

        if (!isLoadingGetPartnerDetran) {
          handleLicensePlateFromQuery(detran);
        }
      }
    }
  }, [detrans]);

  useEffect(() => {
    console.debug("form errors", errors);
  }, [errors]);

  const fetchDebtsVehicle = async (
    data: GetAllDebitsRequest & { detran?: EnumState }
  ) => {
    getDebits({
      ...(!isPublicDepartment && {
        id_establishment: idEstablishment,
        state: data.detran ?? detran.state,
        license_plate: data?.license_plate,
        renavam: data?.renavam,
      }),
      document: normalizeDocument(data?.document || ""),
    });
  };

  const searchAgain = () => {
    setPaymentBilletData(DATA_PAYMENT_DEFAULT);
  };

  const handleShowLogoDetran = (state: string) => {
    const detran = detrans?.data.find(
      (detran) => detran.state === state
    ) as DetranItem;
    setUserId(detran?.id_partner ?? "");
    setDetran(detran);
    clearFormInformations("partial");
  };

  const clearFormInformations = (type: "total" | "partial" = "total") => {
    fields.forEach((field) => {
      if (type === "total" || (type === "partial" && field !== "detran")) {
        setValue(field, "");
      }
    });
    clearErrors();
  };

  const cleanForm = () => {
    clearPaymentBilletData();
    clearFormInformations();
    setQueryParameters(
      isPublicDepartment && public_department
        ? { public_department }
        : {}
    );
    setDetran({} as DetranItem);
  };

  function handleDetranUrlParse(partner: DetranItem) {
    setValue("detran", partner.state);

    setUserId(partner?.id_partner ?? "");

    setDetran(partner);
  }

  const handleLicensePlateFromQuery = async (partner: DetranItem) => {
    const shouldFetchDebts = !dataPaymentBillet?.debits?.length;

    const handleQueries = async (
      query: string,
      queryType: "renavam" | "license_plate"
    ) => {
      cleanForm();
      setValue(queryType, query);
      handleDetranUrlParse(partner);

      if (shouldFetchDebts) {
        await fetchDebtsVehicle({
          [queryType]: query,
          detran: partner.state,
        });
      }
    };

    if (id_detran) {
      handleDetranUrlParse(partner);
    }

    if (renavan_query && id_detran) {
      await handleQueries(renavan_query, "renavam");
    }

    if (license_plate_query && id_detran) {
      await handleQueries(license_plate_query, "license_plate");
    }
  };

  const hasImpeditiveDebit = useMemo(() => {
    return dataPaymentBillet?.debits?.some(
      (debit) => debit.redirect_to_be_paid
    );
  }, [dataPaymentBillet?.debits]);

  const hasAlert = useMemo(() => {
    // const detrans_sp = "1f51d736-4b92-4286-bed1-2be768c5dff2";
    const detrans_sp = "34ca1eba-146d-4d11-8b05-ff5c24cd1120";

    if (dataPartnerSearch.id_parceiro !== detrans_sp) {
      return null;
    }

    return (
      <Alert
        message="Atenção"
        description="A consulta é limitada em até 15 resultados pela SEFAZ. Para visualizar os outros débitos, é necessário o pagamento destes."
        type="warning"
      />
    );
  }, [dataPartnerSearch]);

  function handleNextStep(step: number) {
    if (!hasImpeditiveDebit) {
      setStage(step);
      return;
    }
    setOpenImpeditiveAlert(true);
  }

  const handleDeleteDebit = (id: string) => {
    const debitsDependencies = dataPaymentBillet?.debits.filter(
      (element) => element?.id_linked_debits?.includes(id)
    );
    const currentDebitObj = { id_debit: id };
    const hasDependenciesDebit = Boolean(debitsDependencies?.length);

    if (hasDependenciesDebit) {
      setDebitsForRemove([...debitsDependencies, currentDebitObj]);
      setOpenModalDeleteDebits(true);
      return;
    }

    removeDebit(id);
  };

  const confirmDeleteDebits = () => {
    removeDebit(debitsForRemove.map((debit) => debit.id_debit) as string[]);
    handleClearDeleteDebits();
  };

  const handleClearDeleteDebits = () => {
    setOpenModalDeleteDebits(false);
    setDebitsForRemove([]);
  };

  useEffect(() => clearDetran());

  const isPartner = useMemo(() => {
    return !!client?.id_whitelabel;
  }, [client?.id_whitelabel]);

  const isDebitsAbbleToPayment = useMemo(() => {
    return dataPaymentBillet?.debits?.some(
      (debit) => !debit.redirect_to_be_paid
    )
  }, [dataPaymentBillet])

  return (
    <>
      <S.SearchWrapper enable={enable}>
        <S.WrapperBox>
          <S.Row style={{ padding: 0, justifyContent: "center" }}>
            <S.Title style={{ padding: 0 }}>Pesquise seus débitos</S.Title>
          </S.Row>
          <S.Row hide={!dataPaymentBillet?.debits?.length}>
            <S.WrapperValue hide={!license_plate}>
              <S.LabelValue>Placa</S.LabelValue>
              <S.Value>{license_plate}</S.Value>
            </S.WrapperValue>
            {isPublicDepartment && document &&
              public_department_search_field === EnumPublicDepartmentSearchField.DOCUMENT && (
              <S.WrapperValue>
                <S.LabelValue>
                  {PublicDepartmentSearchFieldLabel[EnumPublicDepartmentSearchField.DOCUMENT]}
                </S.LabelValue>
                <S.Value>{documentMask(document)}</S.Value>
              </S.WrapperValue>
            )}
            {isPublicDepartment && municipal_registration &&
              public_department_search_field === EnumPublicDepartmentSearchField.MUNICIPAL_REGISTRATION && (
              <S.WrapperValue>
                <S.LabelValue>
                  {PublicDepartmentSearchFieldLabel[EnumPublicDepartmentSearchField.MUNICIPAL_REGISTRATION]}
                </S.LabelValue>
                <S.Value>{municipal_registration}</S.Value>
              </S.WrapperValue>
            )}
            {isPublicDepartment && property_registration_number &&
              public_department_search_field === EnumPublicDepartmentSearchField.PROPERTY_REGISTRATION_NUMBER && (
              <S.WrapperValue>
                <S.LabelValue>
                  {PublicDepartmentSearchFieldLabel[EnumPublicDepartmentSearchField.PROPERTY_REGISTRATION_NUMBER]}
                </S.LabelValue>
                <S.Value>{property_registration_number}</S.Value>
              </S.WrapperValue>
            )}
            <S.WrapperValue hide={!dataPartnerSearch.renavam}>
              <S.LabelValue>Renavam</S.LabelValue>
              <S.Value>{dataPartnerSearch.renavam}</S.Value>
            </S.WrapperValue>
          </S.Row>

          {dataPaymentBillet?.debits?.map((debit) => (
            <BankslipItem
              key={debit.id_debit}
              debit={debit}
              activeHover={
                idDebitHovered != null &&
                (idDebitHovered === debit.id_debit ||
                  idDebitHovered === debit.id_linked_debit)
              }
              onHover={setDebitHovered}
              removeBillet={() => handleDeleteDebit(debit.id_debit)}
              defaultOpen="open"
            />
          ))}

          <FormProvider {...methods}>
            {isPublicDepartment && (
              <BoxFormPublicDepartment
                setVariables={setVariables}
                isDisabled={isDisabled}
                idEstablishment={idEstablishment}
                publicDepartment={public_department as EnumPublicDepartaments}
              />
            )}
            {!isPublicDepartment && (
              <BoxFormDetran
                detrans={detrans}
                handleShowLogoDetran={handleShowLogoDetran}
                isDisabled={isDisabled}
                isLoading={isLoadingGetPartnerDetran}
                setVariables={setVariables}
                idEstablishment={idEstablishment}
              />
            )}
          </FormProvider>

          {hasAlert}

          <S.WrapperButton>
            {!isPartner && (
              <S.Button3>
                <Button
                  title="Limpar"
                  type_button="secondary"
                  action={() => cleanForm()}
                />
              </S.Button3>
            )}
            <S.Button3 hide={!dataPaymentBillet?.debits?.length}>
              <Button
                title="Pesquisar novamente"
                type_button="secondary"
                loading={isLoadingGetDebts}
                action={() => searchAgain()}
              />
            </S.Button3>
            <S.Button3>
                <Button
                  data-testid="partner-search-continue-button"
                  title="Continuar"
                  type_button="primary"
                  action={() => handleNextStep(2)}
                  disabled={!isDebitsAbbleToPayment}
                />
            </S.Button3>
          </S.WrapperButton>
        </S.WrapperBox>
      </S.SearchWrapper>
      <ModalAlert
        title="Débitos impeditivos"
        description="Ao clicar em continuar, você confirma ciência de que os débitos impeditivos não são levados para pagamento, eles só podem ser pagos pelo botão de Link para pagamento."
        open={openImpeditiveAlert}
        onCancel={() => setOpenImpeditiveAlert(false)}
        onAccept={() => {
          setStage(2);
          setOpenImpeditiveAlert(false);
        }}
        texts={{
          accept: "Continuar",
        }}
      />
      <ModalDeleteDebits
        debits={debitsForRemove}
        open={openModalDeleteDebits}
        onRemoveAll={confirmDeleteDebits}
        onCancel={handleClearDeleteDebits}
      />
    </>
  );
};
